import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../services/api";

const TOPICS = [
  {
    category: "Emotional Triggers — Fiduciary & Liability",
    icon: "⚖️",
    topics: [
      {
        id: "personal-liability",
        title: "Personal Liability as a Plan Fiduciary",
        prompt: `Write a 1,500-1,800 word blog post for small business plan sponsors about the personal liability they face as 401(k) fiduciaries. The emotional trigger is FEAR — most sponsors don't realize they're personally on the hook, not just their company. Cover ERISA fiduciary duties, real enforcement examples, what "reasonable fees" means legally, and how failing to benchmark fees creates exposure. Tone: urgent but empowering — scare them into action, then show the path forward. End with a call to action to benchmark their plan fees. Target audience: small business owners with 2-50 employee plans.`,
        description: "Fear of personal liability under ERISA — the #1 emotional driver"
      },
      {
        id: "guilt-toward-employees",
        title: "What High Fees Are Really Costing Your Employees",
        prompt: `Write a 1,500-1,800 word blog post targeting small business owners about the guilt of unknowingly subjecting employees to high 401(k) fees. Use a narrative approach — paint the picture of "Maria in accounting" retiring with $40K-$80K less because fees were never reviewed. Cover how fees compound over a career, the difference between 0.5% and 1.5% in expense ratios over 30 years, and the moral responsibility sponsors have. Tone: empathetic but eye-opening. Make it personal — these are people they know. End with a call to action to check their plan's fees.`,
        description: "Guilt — your employees are losing retirement money you could have saved"
      },
      {
        id: "embarrassment-not-knowing",
        title: "The Retirement Plan Problem You Didn't Know You Had",
        prompt: `Write a 1,500-1,800 word blog post for small business plan sponsors about the embarrassment of discovering they've been overpaying 401(k) fees for years. The emotional trigger is the uncomfortable realization that "I should have known this." Cover how the retirement plan industry obscures fees, why brokers and advisors don't always have your best interest, and how easy it is to check. Tone: sympathetic and non-judgmental — normalize the gap in knowledge, then empower action. End with a call to action.`,
        description: "Embarrassment — 'I should have known about this years ago'"
      },
      {
        id: "fiduciary-audit-risk",
        title: "Could Your 401(k) Plan Survive a DOL Audit?",
        prompt: `Write a 1,500-1,800 word blog post for plan sponsors about the risk of a Department of Labor audit. The emotional trigger is ANXIETY — what would happen if the DOL showed up tomorrow and asked for your fee benchmarking documentation? Cover what DOL auditors look for, common deficiencies in small plans, the consequences of non-compliance, and how to create a defensible fiduciary process. Use real DOL enforcement statistics. Tone: informative but urgent. End with practical steps to prepare.`,
        description: "Anxiety — what happens when the DOL comes knocking?"
      }
    ]
  },
  {
    category: "Emotional Triggers — Owner as Participant",
    icon: "💰",
    topics: [
      {
        id: "owner-losing-money",
        title: "You're Paying the Highest Fees in Your Own Plan",
        prompt: `Write a 1,500-1,800 word blog post targeting business owners who also participate in their own 401(k) plans. The emotional trigger is SELF-INTEREST — as the highest contributor, the owner often loses the most money to excessive fees. Cover how asset-based fees disproportionately hit larger account balances, calculate the real dollar impact on a $500K-$1M owner balance over 10-20 years, and compare flat-fee vs. asset-based pricing models. Tone: direct and business-minded — speak to the owner's bottom line. End with a call to action to compare plan costs.`,
        description: "Self-interest — you're losing more than anyone in your own plan"
      },
      {
        id: "owner-retirement-gap",
        title: "The Hidden Tax on Your Retirement: What Every Business Owner Should Know",
        prompt: `Write a 1,500-1,800 word blog post about how hidden 401(k) fees create a "retirement gap" for business owners. Use concrete scenarios: a business owner contributing $23,000/year (max deferral) plus catch-up contributions, with a $750K balance, paying 1.2% in total fees vs. 0.3%. Show the 10-year, 20-year, and 30-year impact. The emotional trigger is LOSS AVERSION — show what they're giving up. Tone: analytical but accessible. Include a simple calculation readers can replicate. End with a call to action.`,
        description: "Loss aversion — quantify exactly what you're giving up"
      },
      {
        id: "owner-missed-opportunity",
        title: "What Could You Do With an Extra $200K at Retirement?",
        prompt: `Write a 1,500-1,800 word blog post that flips the script — instead of focusing on what fees are costing business owners, focus on what they could DO with the savings. The emotional trigger is ASPIRATION — paint the picture of what an extra $100K-$300K at retirement looks like. More travel? Earlier retirement? A legacy for your family? Use real fee differential calculations to show how switching from a high-fee to a flat-fee plan compounds into life-changing money. Tone: optimistic and forward-looking. End with a call to action.`,
        description: "Aspiration — imagine what you'd do with an extra $200K at retirement"
      }
    ]
  },
  {
    category: "Logical Triggers — Data & Evidence",
    icon: "📊",
    topics: [
      {
        id: "fee-comparison-data",
        title: "How Much Are 401(k) Plans Really Costing? The Data Behind the Fees",
        prompt: `Write a 1,500-1,800 word blog post presenting real data on 401(k) plan costs across American businesses. Use Form 5500 filing data as the authoritative source. Cover: average cost per participant by plan size, how fees vary by industry, the gap between the cheapest and most expensive plans, and trends over the last 5 years. The trigger is CREDIBILITY — this isn't opinion, it's government-reported data. Tone: authoritative and data-driven. Include specific statistics and benchmarks. End with a call to action to see where their plan ranks.`,
        description: "Credibility — real government data, not marketing claims"
      },
      {
        id: "flat-fee-vs-asset-based",
        title: "Flat Fee vs. Asset-Based: Which 401(k) Pricing Model Saves You More?",
        prompt: `Write a 1,500-1,800 word blog post comparing flat-fee and asset-based 401(k) pricing models. Use concrete examples with real numbers: a 15-person plan with $2M in assets under both models. Show how asset-based fees grow as assets grow, while flat fees remain predictable. Cover the math of basis points, the "hidden tax" effect, and when each model makes sense. The trigger is RATIONALITY — help them make a data-driven decision. Tone: balanced and educational but ultimately showing the advantages of flat-fee for most small plans. End with a call to action.`,
        description: "Rationality — a side-by-side comparison that makes the choice obvious"
      },
      {
        id: "industry-benchmark",
        title: "Is Your Plan Overpriced? Industry Benchmarks for 401(k) Fees",
        prompt: `Write a 1,500-1,800 word blog post that helps plan sponsors benchmark their 401(k) costs against peers in their industry. Cover how to read Form 5500 data, what "cost per participant" means, how to factor in asset-based vs. flat fees, and what constitutes a "reasonable" fee for plans with 2-50 participants. The trigger is COMPARISON — everyone wants to know how they stack up. Tone: practical and actionable. Include benchmark ranges by plan size. End with a call to action to get a free plan analysis.`,
        description: "Comparison — how does your plan stack up against peers?"
      },
      {
        id: "compounding-cost",
        title: "The $100,000 Mistake: How Small Fee Differences Compound Over a Career",
        prompt: `Write a 1,500-1,800 word blog post about the compounding effect of 401(k) fees over a working career. Use vivid, specific math: show how a seemingly small fee difference (e.g., 0.75% vs. 0.25%) on a $50,000 salary with 6% contribution + 3% match grows into a six-figure gap over 25-30 years. Include a year-by-year breakdown for at least the first 10 years and then key milestones. The trigger is SHOCK VALUE — the numbers are eye-opening when you actually run them. Tone: educational and compelling. End with a call to action.`,
        description: "Shock value — the math most people never see"
      }
    ]
  },
  {
    category: "Logical Triggers — Process & Trust",
    icon: "🔍",
    topics: [
      {
        id: "who-benefits-from-high-fees",
        title: "Who Really Benefits When Your 401(k) Fees Are High?",
        prompt: `Write a 1,500-1,800 word blog post exposing the fee ecosystem in the 401(k) industry. Cover who gets paid and how: recordkeepers, TPAs, financial advisors, fund companies, and brokers. Explain revenue sharing, 12b-1 fees, and wrap fees. The trigger is DISTRUST — once sponsors understand the incentive structure, they question their current setup. Tone: investigative journalism style — fair but revealing. Don't demonize advisors, but illuminate conflicts of interest. End with a call to action to request a fee transparency audit.`,
        description: "Distrust — follow the money to see who benefits from your high fees"
      },
      {
        id: "switching-is-easier",
        title: "Switching Your 401(k) Provider: It's Easier Than You Think",
        prompt: `Write a 1,500-1,800 word blog post that addresses the #1 objection to changing 401(k) providers: "It's too complicated." Walk through the actual process step by step, typical timelines (60-90 days), what participants experience, and what the plan sponsor needs to do. Address common fears: "Will there be a blackout period?" "Will my employees be confused?" "Is it expensive to switch?" The trigger is REMOVING FRICTION — show that the barrier to action is much lower than expected. Tone: reassuring and practical. End with a call to action.`,
        description: "Friction removal — the switch is easier than you think"
      },
      {
        id: "advisor-conflicts",
        title: "Is Your Financial Advisor Really Looking Out for Your Plan?",
        prompt: `Write a 1,500-1,800 word blog post about potential conflicts of interest that financial advisors have when recommending 401(k) plans. Cover the difference between fiduciary advisors and suitability-standard brokers, how commission-based compensation influences fund selection, and why "free" plan advice often costs more in the long run. The trigger is SKEPTICISM — empower sponsors to ask the right questions. Tone: educational, not accusatory. Provide specific questions to ask an advisor. End with a call to action.`,
        description: "Skepticism — ask your advisor these uncomfortable questions"
      },
      {
        id: "form-5500-transparency",
        title: "Your 401(k) Plan's Report Card Is Public — Here's How to Read It",
        prompt: `Write a 1,500-1,800 word blog post explaining Form 5500 filings in plain English. Cover what data is reported, where to find your plan's filing, what the key metrics mean (total expenses, admin fees, participant count, assets), and how to use this data to evaluate your plan. The trigger is TRANSPARENCY — this is public data anyone can access. Tone: empowering and demystifying. Make government data feel accessible. End with a call to action to look up their plan.`,
        description: "Transparency — your plan's data is public; here's what it says"
      }
    ]
  },
  {
    category: "Seasonal & Timely Topics",
    icon: "📅",
    topics: [
      {
        id: "year-end-review",
        title: "Year-End 401(k) Checklist: What Every Plan Sponsor Should Review",
        prompt: `Write a 1,500-1,800 word blog post for plan sponsors on year-end 401(k) plan review tasks. Cover fee benchmarking, compliance testing deadlines, contribution limit updates, participant communication requirements, and Form 5500 filing preparation. The trigger is TIMELINESS — create urgency around the calendar. Tone: practical and organized. Include a downloadable-style checklist format within the content. End with a call to action.`,
        description: "Urgency — year-end deadlines are approaching"
      },
      {
        id: "new-year-resolution",
        title: "Make 2025 the Year You Fix Your Company's 401(k)",
        prompt: `Write a 1,500-1,800 word blog post framing 401(k) optimization as a New Year's resolution for business owners. Cover: quick wins (fee benchmarking), medium-term improvements (auto-enrollment, auto-escalation), and long-term strategy (switching to a flat-fee provider). The trigger is FRESH START — leverage the psychological reset of a new year. Tone: motivational and action-oriented. Include a "30-60-90 day plan" for improving the company 401(k). End with a call to action.`,
        description: "Fresh start — new year, new approach to your plan"
      },
      {
        id: "secure-act-updates",
        title: "New 401(k) Rules: What SECURE Act 2.0 Means for Your Small Business",
        prompt: `Write a 1,500-1,800 word blog post covering the most impactful SECURE Act 2.0 provisions for small businesses. Focus on: increased tax credits for new plans, mandatory auto-enrollment (starting 2025), Roth employer contributions, student loan matching, and emergency savings accounts. The trigger is REGULATION — stay compliant and take advantage of new incentives. Tone: informative and practical. Highlight the financial benefits of each provision. End with a call to action.`,
        description: "Compliance — new laws mean new opportunities and obligations"
      },
      {
        id: "tax-credit-startup",
        title: "Start a 401(k) and the Government Pays for It: Tax Credits You're Missing",
        prompt: `Write a 1,500-1,800 word blog post about the generous tax credits available for small businesses that start new 401(k) plans under SECURE Act 2.0. Cover: up to $5,000/year for 3 years in startup credits, $1,000/employee auto-enrollment credit, how these credits can fully offset (and even exceed) the cost of running a plan. The trigger is FREE MONEY — most small business owners don't know about these credits. Tone: excited and informative. Include specific examples for 5, 10, and 20-person companies. End with a call to action.`,
        description: "Free money — tax credits that more than pay for your plan"
      }
    ]
  }
];

// Use the same API base URL as the rest of the app
const API_BASE = API_BASE_URL;

export default function BlogWriter() {
  const navigate = useNavigate();
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [naicsSearch, setNaicsSearch] = useState("");
  const [selectedNaics, setSelectedNaics] = useState(null);
  const [naicsResults, setNaicsResults] = useState([]);
  const [showNaicsDropdown, setShowNaicsDropdown] = useState(false);
  const [generatedPost, setGeneratedPost] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [industryData, setIndustryData] = useState(null);
  const [overallData, setOverallData] = useState(null);
  const [wordCount, setWordCount] = useState(0);
  const [copySuccess, setCopySuccess] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [loadingData, setLoadingData] = useState(false);
  const [customPromptAddition, setCustomPromptAddition] = useState("");
  const outputRef = useRef(null);
  const naicsRef = useRef(null);

  // Close NAICS dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (naicsRef.current && !naicsRef.current.contains(e.target)) {
        setShowNaicsDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Search NAICS codes
  useEffect(() => {
    if (naicsSearch.length < 2) {
      setNaicsResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`${API_BASE}/naics/search?search_term=${encodeURIComponent(naicsSearch)}`);
        if (res.ok) {
          const data = await res.json();
          setNaicsResults(data);
          setShowNaicsDropdown(true);
        }
      } catch {
        setNaicsResults([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [naicsSearch]);

  // Fetch industry data when NAICS selected
  const fetchIndustryData = async (naicsCode) => {
    setLoadingData(true);
    try {
      const res = await fetch(`${API_BASE}/blog/industry-stats/${naicsCode}`);
      if (res.ok) {
        const data = await res.json();
        setIndustryData(data);
      }
    } catch {
      setIndustryData(null);
    }
    setLoadingData(false);
  };

  // Fetch overall stats on mount
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/blog/overall-stats`);
        if (res.ok) {
          const data = await res.json();
          setOverallData(data);
        }
      } catch {
        // Will use hardcoded fallbacks
        setOverallData({
          total_plans: 813000,
          avg_cost_per_participant: 396,
          median_cost_per_participant: 126,
          total_filings: 13800000,
          years_covered: "2009-2024"
        });
      }
    })();
  }, []);

  const selectNaics = (item) => {
    setSelectedNaics(item);
    setNaicsSearch(`${item.business_code} — ${item.business_description}`);
    setShowNaicsDropdown(false);
    fetchIndustryData(item.business_code);
  };

  const clearNaics = () => {
    setSelectedNaics(null);
    setNaicsSearch("");
    setIndustryData(null);
    setNaicsResults([]);
  };

  const buildPrompt = () => {
    if (!selectedTopic) return "";

    let prompt = selectedTopic.prompt;

    // Add industry context if NAICS selected
    if (selectedNaics && industryData) {
      prompt += `\n\n--- INDUSTRY-SPECIFIC CONTEXT ---
This blog post should be specifically tailored to the ${industryData.industry_name || selectedNaics.business_description} industry (NAICS ${selectedNaics.business_code}).

Use this real data from Form 5500 filings for this industry:
- Number of plans in this industry: ${industryData.total_plans?.toLocaleString() || "N/A"}
- Average cost per participant: $${industryData.avg_cost_per_participant?.toFixed(2) || "N/A"}
- Median cost per participant: $${industryData.median_cost_per_participant?.toFixed(2) || "N/A"}
- 75th percentile cost: $${industryData.p75_cost?.toFixed(2) || "N/A"}
- 25th percentile cost: $${industryData.p25_cost?.toFixed(2) || "N/A"}
- Average plan size (participants): ${industryData.avg_participants?.toFixed(0) || "N/A"}
- Average plan assets: $${industryData.avg_assets ? (industryData.avg_assets / 1000000).toFixed(1) + "M" : "N/A"}
- Plans with cost > $500/participant: ${industryData.high_cost_plans || "N/A"} (${industryData.high_cost_pct?.toFixed(1) || "N/A"}%)
${industryData.avg_employer_contrib ? `- Average employer contribution per participant: $${industryData.avg_employer_contrib.toFixed(0)}` : ""}

Weave these real data points naturally into the narrative. Reference them as "according to DOL Form 5500 filings" or "government data shows." Make specific comparisons like "the average ${industryData.industry_name || selectedNaics.business_description} plan pays $X per participant — but the best plans in your industry pay only $Y."

Address the reader as a ${industryData.industry_name || selectedNaics.business_description} business owner specifically. Use industry-relevant examples, scenarios, and language.`;
    }

    // Add overall data context
    if (overallData) {
      prompt += `\n\n--- GENERAL DATABASE CONTEXT ---
Background data from our Form 5500 database (use where relevant):
- Database covers ${overallData.total_filings?.toLocaleString() || "13.8 million"}+ filings from ${overallData.years_covered || "2009-2024"}
- ${overallData.total_plans?.toLocaleString() || "813,000"}+ unique plans tracked
- Overall average cost per participant: $${overallData.avg_cost_per_participant?.toFixed(0) || "396"}
- Overall median cost per participant: $${overallData.median_cost_per_participant?.toFixed(0) || "126"}
- Note: the large gap between mean ($396) and median ($126) indicates significant outliers — many plans are paying far more than necessary.`;
    }

    // Add custom instructions
    if (customPromptAddition.trim()) {
      prompt += `\n\n--- ADDITIONAL INSTRUCTIONS ---\n${customPromptAddition}`;
    }

    prompt += `\n\n--- FORMATTING REQUIREMENTS ---
- Write exactly 1,500-1,800 words
- Use compelling headers and subheaders (H2, H3) to break up the content
- Include a strong opening hook in the first paragraph
- End with a clear call to action directing readers to benchmark their plan at LoveYour401K.com
- Write in a professional but approachable tone — like a knowledgeable friend explaining something important
- Format in markdown
- Do not use generic stock-photo language or AI cliches
- Include at least one specific data example or calculation
- Reference Form 5500 data as the source where appropriate`;

    return prompt;
  };

  const generatePost = async () => {
    if (!selectedTopic) return;
    setIsGenerating(true);
    setGeneratedPost("");
    setWordCount(0);

    const prompt = buildPrompt();

    try {
      const response = await fetch(`${API_BASE}/blog/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ prompt })
      });

      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }

      const data = await response.json();
      const text = data.content || "";

      setGeneratedPost(text || "Error generating post. Please try again.");
      setWordCount(text ? text.split(/\s+/).length : 0);

      setTimeout(() => {
        outputRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 200);
    } catch (err) {
      setGeneratedPost("Error: " + err.message);
    }

    setIsGenerating(false);
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(generatedPost);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch {
      // Fallback
      const el = document.createElement("textarea");
      el.value = generatedPost;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };

  // Render markdown-ish content (simple renderer)
  const renderMarkdown = (text) => {
    if (!text) return null;
    const lines = text.split("\n");
    return lines.map((line, i) => {
      if (line.startsWith("### ")) return <h3 key={i} style={styles.h3}>{line.replace("### ", "")}</h3>;
      if (line.startsWith("## ")) return <h2 key={i} style={styles.h2}>{line.replace("## ", "")}</h2>;
      if (line.startsWith("# ")) return <h1 key={i} style={styles.h1}>{line.replace("# ", "")}</h1>;
      if (line.startsWith("**") && line.endsWith("**")) return <p key={i} style={styles.bold}>{line.replace(/\*\*/g, "")}</p>;
      if (line.startsWith("- ")) return <li key={i} style={styles.li}>{line.replace("- ", "")}</li>;
      if (line.trim() === "") return <br key={i} />;
      // Handle inline bold
      const parts = line.split(/(\*\*[^*]+\*\*)/g);
      return (
        <p key={i} style={styles.paragraph}>
          {parts.map((part, j) =>
            part.startsWith("**") && part.endsWith("**")
              ? <strong key={j}>{part.replace(/\*\*/g, "")}</strong>
              : part
          )}
        </p>
      );
    });
  };

  return (
    <div style={styles.container}>
      {/* Site Navigation Header */}
      <header style={styles.siteHeader}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 2.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div
              style={{ fontSize: '1.5rem', fontWeight: 600, color: '#1e3a8a', letterSpacing: '0.02em', cursor: 'pointer' }}
              onClick={() => navigate('/')}
            >
              Love Your 401k
            </div>
            <div style={{ display: 'flex', gap: '2.5rem', alignItems: 'center' }}>
              <a href="/" style={{ color: '#6b7280', textDecoration: 'none', fontSize: '0.9375rem' }}>Search Plans</a>
              <a href="/estimator" style={{ color: '#6b7280', textDecoration: 'none', fontSize: '0.9375rem' }}>What Should I Pay for a 401(k)?</a>
              <a href="/admin/research" style={{ color: '#6b7280', textDecoration: 'none', fontSize: '0.9375rem' }}>401(k) Research</a>
              <a href="/admin/prospecting" style={{ color: '#6b7280', textDecoration: 'none', fontSize: '0.9375rem' }}>Prospecting</a>
              <a href="/admin/blog-writer" style={{ color: '#1e3a8a', textDecoration: 'none', fontSize: '0.9375rem', fontWeight: 600 }}>Blog Writer</a>
              <a href="/admin/cohort" style={{ color: '#6b7280', textDecoration: 'none', fontSize: '0.9375rem' }}>Cohort Analysis</a>
              <a href="/faq" style={{ color: '#6b7280', textDecoration: 'none', fontSize: '0.9375rem' }}>FAQ</a>
            </div>
          </div>
        </div>
      </header>

      {/* Page Header */}
      <div style={styles.header}>
        <div style={styles.headerInner}>
          <h1 style={styles.title}>401(k) Blog Writer</h1>
          <p style={styles.subtitle}>
            AI-powered content generation backed by 13M+ Form 5500 filings
          </p>
          {overallData && (
            <div style={styles.statBar}>
              <div style={styles.statItem}>
                <span style={styles.statValue}>{(overallData.total_filings / 1000000).toFixed(1)}M+</span>
                <span style={styles.statLabel}>Filings</span>
              </div>
              <div style={styles.statDivider} />
              <div style={styles.statItem}>
                <span style={styles.statValue}>{(overallData.total_plans / 1000).toFixed(0)}K+</span>
                <span style={styles.statLabel}>Plans</span>
              </div>
              <div style={styles.statDivider} />
              <div style={styles.statItem}>
                <span style={styles.statValue}>${overallData.avg_cost_per_participant?.toFixed(0)}</span>
                <span style={styles.statLabel}>Avg Cost/Participant</span>
              </div>
              <div style={styles.statDivider} />
              <div style={styles.statItem}>
                <span style={styles.statValue}>${overallData.median_cost_per_participant?.toFixed(0)}</span>
                <span style={styles.statLabel}>Median Cost</span>
              </div>
            </div>
          )}
        </div>
      </div>

      <div style={styles.mainContent}>
        {/* Left Column — Topic Selection */}
        <div style={styles.leftCol}>
          <div style={styles.sectionCard}>
            <h2 style={styles.sectionTitle}>Select a Blog Topic</h2>
            <p style={styles.sectionDesc}>
              Choose from research-backed topics designed to resonate with plan sponsors.
            </p>

            {TOPICS.map((cat) => (
              <div key={cat.category} style={styles.categoryBlock}>
                <button
                  onClick={() => setExpandedCategory(expandedCategory === cat.category ? null : cat.category)}
                  style={{
                    ...styles.categoryHeader,
                    ...(expandedCategory === cat.category ? styles.categoryHeaderActive : {})
                  }}
                >
                  <span>
                    <span style={styles.categoryIcon}>{cat.icon}</span>
                    {cat.category}
                  </span>
                  <span style={styles.chevron}>
                    {expandedCategory === cat.category ? "▾" : "▸"}
                  </span>
                </button>
                {expandedCategory === cat.category && (
                  <div style={styles.topicList}>
                    {cat.topics.map((topic) => (
                      <button
                        key={topic.id}
                        onClick={() => setSelectedTopic(topic)}
                        style={{
                          ...styles.topicButton,
                          ...(selectedTopic?.id === topic.id ? styles.topicButtonActive : {})
                        }}
                      >
                        <div style={styles.topicTitle}>{topic.title}</div>
                        <div style={styles.topicDesc}>{topic.description}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Right Column — Configuration & Output */}
        <div style={styles.rightCol}>
          {/* Selected Topic Display */}
          {selectedTopic && (
            <div style={styles.selectedCard}>
              <div style={styles.selectedHeader}>
                <span style={styles.selectedBadge}>Selected Topic</span>
                <button onClick={() => setSelectedTopic(null)} style={styles.clearBtn}>✕</button>
              </div>
              <h3 style={styles.selectedTitle}>{selectedTopic.title}</h3>
              <p style={styles.selectedDesc}>{selectedTopic.description}</p>
            </div>
          )}

          {/* NAICS Industry Filter */}
          <div style={styles.sectionCard}>
            <h2 style={styles.sectionTitle}>Industry Filter (Optional)</h2>
            <p style={styles.sectionDesc}>
              Select a NAICS code to tailor the blog post with industry-specific data from your Form 5500 database.
            </p>

            <div ref={naicsRef} style={styles.naicsContainer}>
              <div style={styles.inputRow}>
                <input
                  type="text"
                  value={naicsSearch}
                  onChange={(e) => setNaicsSearch(e.target.value)}
                  placeholder="Search by industry name or NAICS code..."
                  style={styles.input}
                />
                {selectedNaics && (
                  <button onClick={clearNaics} style={styles.clearNaicsBtn}>Clear</button>
                )}
              </div>

              {showNaicsDropdown && naicsResults.length > 0 && (
                <div style={styles.dropdown}>
                  {naicsResults.map((item, i) => (
                    <button
                      key={i}
                      onClick={() => selectNaics(item)}
                      style={styles.dropdownItem}
                    >
                      <span style={styles.naicsCode}>{item.business_code}</span>
                      <span>{item.business_description}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Industry Data Preview */}
            {loadingData && (
              <div style={styles.loadingRow}>
                <div style={styles.spinner} /> Loading industry data...
              </div>
            )}
            {industryData && !loadingData && (
              <div style={styles.dataPreview}>
                <h4 style={styles.dataPreviewTitle}>
                  {industryData.industry_name || selectedNaics?.business_description} — Data Preview
                </h4>
                <div style={styles.dataGrid}>
                  <DataStat label="Plans" value={industryData.total_plans?.toLocaleString()} />
                  <DataStat label="Avg Cost/Participant" value={`$${industryData.avg_cost_per_participant?.toFixed(0)}`} />
                  <DataStat label="Median Cost" value={`$${industryData.median_cost_per_participant?.toFixed(0)}`} />
                  <DataStat label="Avg Plan Size" value={`${industryData.avg_participants?.toFixed(0)} people`} />
                  <DataStat label="Avg Assets" value={industryData.avg_assets ? `$${(industryData.avg_assets / 1000000).toFixed(1)}M` : "N/A"} />
                  <DataStat label="High-Cost Plans" value={`${industryData.high_cost_pct?.toFixed(1)}%`} highlight />
                </div>
              </div>
            )}
          </div>

          {/* Custom Instructions */}
          <div style={styles.sectionCard}>
            <h2 style={styles.sectionTitle}>Custom Instructions (Optional)</h2>
            <textarea
              value={customPromptAddition}
              onChange={(e) => setCustomPromptAddition(e.target.value)}
              placeholder="Add any additional instructions... e.g., 'Focus on plans with fewer than 10 employees' or 'Include a comparison to Vanguard and Fidelity pricing' or 'Write for a dental practice audience'"
              style={styles.textarea}
              rows={3}
            />
          </div>

          {/* Generate Button */}
          <button
            onClick={generatePost}
            disabled={!selectedTopic || isGenerating}
            style={{
              ...styles.generateBtn,
              ...(!selectedTopic || isGenerating ? styles.generateBtnDisabled : {})
            }}
          >
            {isGenerating ? (
              <span style={styles.generatingRow}>
                <div style={styles.spinnerWhite} />
                Generating blog post...
              </span>
            ) : (
              `Generate Blog Post${selectedNaics ? ` for ${selectedNaics.business_description}` : ""}`
            )}
          </button>

          {/* Output */}
          {generatedPost && (
            <div ref={outputRef} style={styles.outputCard}>
              <div style={styles.outputHeader}>
                <div>
                  <h2 style={styles.outputTitle}>Generated Blog Post</h2>
                  <span style={styles.wordCountBadge}>{wordCount.toLocaleString()} words</span>
                  {selectedNaics && (
                    <span style={styles.industryBadge}>
                      {selectedNaics.business_description}
                    </span>
                  )}
                </div>
                <button onClick={copyToClipboard} style={styles.copyBtn}>
                  {copySuccess ? "Copied!" : "Copy"}
                </button>
              </div>
              <div style={styles.outputBody}>
                {renderMarkdown(generatedPost)}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function DataStat({ label, value, highlight }) {
  return (
    <div style={{ ...styles.dataStat, ...(highlight ? styles.dataStatHighlight : {}) }}>
      <div style={styles.dataStatValue}>{value || "—"}</div>
      <div style={styles.dataStatLabel}>{label}</div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: "100vh",
    background: "#fafbfc",
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    color: "#111827"
  },
  siteHeader: {
    background: "white",
    borderBottom: "1px solid #e5e7eb",
    padding: "1rem 0",
    position: "sticky",
    top: 0,
    zIndex: 10
  },
  header: {
    background: "white",
    borderBottom: "1px solid #e5e7eb",
    padding: "24px 0"
  },
  headerInner: {
    maxWidth: 1400,
    margin: "0 auto",
    padding: "0 32px"
  },
  title: {
    fontSize: "2rem",
    fontWeight: 800,
    color: "#111827",
    margin: "0 0 0.25rem 0",
    letterSpacing: "-0.02em"
  },
  subtitle: {
    fontSize: 14,
    color: "#6b7280",
    margin: "4px 0 16px 0"
  },
  statBar: {
    display: "flex",
    alignItems: "center",
    gap: 20,
    background: "#f3f4f6",
    borderRadius: 10,
    padding: "12px 24px"
  },
  statItem: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center"
  },
  statValue: {
    fontSize: 18,
    fontWeight: 700,
    color: "#1e3a8a"
  },
  statLabel: {
    fontSize: 11,
    color: "#6b7280",
    textTransform: "uppercase",
    letterSpacing: "0.5px"
  },
  statDivider: {
    width: 1,
    height: 32,
    background: "#e5e7eb"
  },
  mainContent: {
    display: "flex",
    gap: 24,
    maxWidth: 1400,
    margin: "24px auto",
    padding: "0 32px",
    alignItems: "flex-start"
  },
  leftCol: {
    width: 420,
    flexShrink: 0
  },
  rightCol: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: 16
  },
  sectionCard: {
    background: "white",
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    padding: 20
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 600,
    color: "#111827",
    margin: "0 0 6px 0"
  },
  sectionDesc: {
    fontSize: 13,
    color: "#6b7280",
    margin: "0 0 16px 0",
    lineHeight: 1.5
  },
  categoryBlock: {
    marginBottom: 4
  },
  categoryHeader: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "10px 14px",
    background: "#f9fafb",
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    color: "#374151",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.15s"
  },
  categoryHeaderActive: {
    background: "#eff6ff",
    borderColor: "#93c5fd",
    color: "#1e3a8a"
  },
  categoryIcon: {
    marginRight: 8,
    fontSize: 15
  },
  chevron: {
    fontSize: 12,
    opacity: 0.5
  },
  topicList: {
    padding: "6px 0 6px 12px",
    display: "flex",
    flexDirection: "column",
    gap: 4
  },
  topicButton: {
    textAlign: "left",
    padding: "10px 14px",
    background: "transparent",
    border: "1px solid transparent",
    borderRadius: 8,
    cursor: "pointer",
    transition: "all 0.15s",
    color: "#374151"
  },
  topicButtonActive: {
    background: "#eff6ff",
    borderColor: "#93c5fd",
    color: "#111827"
  },
  topicTitle: {
    fontSize: 13,
    fontWeight: 600,
    marginBottom: 3
  },
  topicDesc: {
    fontSize: 11,
    color: "#9ca3af",
    lineHeight: 1.4
  },
  selectedCard: {
    background: "#eff6ff",
    border: "1px solid #93c5fd",
    borderRadius: 12,
    padding: 20
  },
  selectedHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 8
  },
  selectedBadge: {
    fontSize: 11,
    fontWeight: 600,
    color: "#1e3a8a",
    textTransform: "uppercase",
    letterSpacing: "0.5px"
  },
  clearBtn: {
    background: "#f3f4f6",
    border: "1px solid #e5e7eb",
    color: "#6b7280",
    borderRadius: 6,
    width: 24,
    height: 24,
    cursor: "pointer",
    fontSize: 12
  },
  selectedTitle: {
    fontSize: 18,
    fontWeight: 700,
    color: "#111827",
    margin: "0 0 6px 0"
  },
  selectedDesc: {
    fontSize: 13,
    color: "#6b7280",
    margin: 0,
    lineHeight: 1.5
  },
  naicsContainer: {
    position: "relative"
  },
  inputRow: {
    display: "flex",
    gap: 8
  },
  input: {
    flex: 1,
    padding: "10px 14px",
    background: "white",
    border: "1px solid #d1d5db",
    borderRadius: 8,
    color: "#111827",
    fontSize: 14,
    outline: "none"
  },
  clearNaicsBtn: {
    padding: "10px 16px",
    background: "#f3f4f6",
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    color: "#6b7280",
    cursor: "pointer",
    fontSize: 13
  },
  dropdown: {
    position: "absolute",
    top: "calc(100% + 4px)",
    left: 0,
    right: 0,
    background: "white",
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    maxHeight: 240,
    overflowY: "auto",
    zIndex: 50,
    boxShadow: "0 4px 16px rgba(0,0,0,0.1)"
  },
  dropdownItem: {
    width: "100%",
    textAlign: "left",
    padding: "10px 14px",
    background: "transparent",
    border: "none",
    borderBottom: "1px solid #f3f4f6",
    color: "#374151",
    cursor: "pointer",
    fontSize: 13,
    display: "flex",
    gap: 12,
    alignItems: "center",
    transition: "background 0.1s"
  },
  naicsCode: {
    fontFamily: "monospace",
    fontSize: 12,
    color: "#1e3a8a",
    fontWeight: 600,
    background: "#eff6ff",
    padding: "2px 8px",
    borderRadius: 4,
    flexShrink: 0
  },
  loadingRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    color: "#6b7280",
    fontSize: 13,
    marginTop: 12
  },
  spinner: {
    width: 16,
    height: 16,
    border: "2px solid #dbeafe",
    borderTopColor: "#1e3a8a",
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite"
  },
  spinnerWhite: {
    width: 18,
    height: 18,
    border: "2px solid rgba(255,255,255,0.3)",
    borderTopColor: "#fff",
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite"
  },
  dataPreview: {
    marginTop: 16,
    background: "#f0f9ff",
    border: "1px solid #bae6fd",
    borderRadius: 10,
    padding: 16
  },
  dataPreviewTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: "#1e3a8a",
    margin: "0 0 12px 0"
  },
  dataGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: 10
  },
  dataStat: {
    background: "#f9fafb",
    borderRadius: 8,
    padding: "10px 12px",
    textAlign: "center",
    border: "1px solid #e5e7eb"
  },
  dataStatHighlight: {
    background: "#fef2f2",
    border: "1px solid #fecaca"
  },
  dataStatValue: {
    fontSize: 18,
    fontWeight: 700,
    color: "#111827"
  },
  dataStatLabel: {
    fontSize: 11,
    color: "#9ca3af",
    marginTop: 2
  },
  textarea: {
    width: "100%",
    padding: "10px 14px",
    background: "white",
    border: "1px solid #d1d5db",
    borderRadius: 8,
    color: "#111827",
    fontSize: 14,
    fontFamily: "inherit",
    resize: "vertical",
    outline: "none",
    lineHeight: 1.5,
    boxSizing: "border-box"
  },
  generateBtn: {
    width: "100%",
    padding: "16px 24px",
    background: "#1e3a8a",
    border: "none",
    borderRadius: 10,
    color: "#fff",
    fontSize: 16,
    fontWeight: 700,
    cursor: "pointer",
    transition: "all 0.2s",
    letterSpacing: "-0.3px"
  },
  generateBtnDisabled: {
    opacity: 0.4,
    cursor: "not-allowed"
  },
  generatingRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 10
  },
  outputCard: {
    background: "white",
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    overflow: "hidden"
  },
  outputHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "16px 20px",
    borderBottom: "1px solid #e5e7eb",
    flexWrap: "wrap",
    gap: 8
  },
  outputTitle: {
    fontSize: 16,
    fontWeight: 600,
    color: "#111827",
    margin: 0,
    display: "inline"
  },
  wordCountBadge: {
    marginLeft: 10,
    fontSize: 12,
    fontWeight: 600,
    color: "#1e3a8a",
    background: "#eff6ff",
    padding: "3px 10px",
    borderRadius: 20
  },
  industryBadge: {
    marginLeft: 8,
    fontSize: 12,
    fontWeight: 600,
    color: "#b45309",
    background: "#fffbeb",
    padding: "3px 10px",
    borderRadius: 20
  },
  copyBtn: {
    padding: "8px 16px",
    background: "#f3f4f6",
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    color: "#374151",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 500,
    transition: "all 0.15s"
  },
  outputBody: {
    padding: "24px 28px",
    maxHeight: 700,
    overflowY: "auto",
    lineHeight: 1.75,
    fontSize: 15,
    color: "#374151"
  },
  h1: {
    fontSize: 28,
    fontWeight: 700,
    color: "#111827",
    margin: "24px 0 12px 0",
    lineHeight: 1.3
  },
  h2: {
    fontSize: 22,
    fontWeight: 700,
    color: "#1e3a8a",
    margin: "28px 0 10px 0",
    lineHeight: 1.3
  },
  h3: {
    fontSize: 17,
    fontWeight: 600,
    color: "#1e40af",
    margin: "20px 0 8px 0"
  },
  paragraph: {
    margin: "0 0 12px 0",
    lineHeight: 1.75
  },
  bold: {
    fontWeight: 700,
    color: "#111827",
    margin: "0 0 12px 0"
  },
  li: {
    marginBottom: 6,
    paddingLeft: 8,
    lineHeight: 1.6
  }
};

// Inject keyframe animation
if (typeof document !== "undefined") {
  const styleEl = document.createElement("style");
  styleEl.textContent = `@keyframes spin { to { transform: rotate(360deg); } }`;
  document.head.appendChild(styleEl);
}
